import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  FaCopy,
  FaChevronDown,
  FaRegEye,
  FaAlignLeft,
  FaAlignJustify,
} from "react-icons/fa";
import toast from "react-hot-toast";
import LivePreview from "./LivePreview";

const ExampleCode = () => {
  const [fakeData, setFakeData] = useState({});
  const [showFakeData, setShowFakeData] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [error, setError] = useState(null);

  const [selectedEndpoint, setSelectedEndpoint] = useState("/products");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLivePreviewOpen, setIsLivePreviewOpen] = useState(false);
  const [isLineWrapped, setIsLineWrapped] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("javascript");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const endpoints = [
    { label: "Products", value: "/products" },
    { label: "Single Product", value: "/products/1" },
    { label: "Categories", value: "/categories" },
    { label: "Amazon Products", value: "/amazonproducts" },
    { label: "Walmart Products", value: "/walmartproducts" },
    { label: "Users", value: "/users" },
    { label: "Single User", value: "/users/1" },
    { label: "Wishlists", value: "/wishlists" },
    { label: "Reviews", value: "/reviews" },
    { label: "Orders", value: "/orders" },
    { label: "Carts", value: "/carts" },
    { label: "Payments", value: "/payments" },
    { label: "Coupons", value: "/coupons" },
    { label: "Posts", value: "/posts" },
    { label: "Comments", value: "/comments" },
    { label: "Photos", value: "/photos" },
    { label: "Todos", value: "/todos" },
    { label: "Notifications", value: "/notifications" },
  ];

  const handleFakeData = async () => {
    setLoadingState(true);
    setError(null);

    // Check if the endpoint data is already cached
    const cacheKey = `fakestore_api_cache_${selectedEndpoint}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      setFakeData(JSON.parse(cachedData));
      setShowFakeData(true);
      // Faster loading state for cached responses
      setTimeout(() => setLoadingState(false), 300);
      return;
    }

    try {
      const response = await axios.get(
        `https://fakestoreapi.noksha.dev/api${selectedEndpoint}`,
      );
      setFakeData(response.data);
      sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      setShowFakeData(true);
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
    } finally {
      setTimeout(() => setLoadingState(false), 1000);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Code copied to clipboard!", {
      position: "top-right",
      duration: 3000,
      style: {
        background: "#2d3338",
        color: "#fff",
        border: "1px solid #ecb501",
      },
      iconTheme: {
        primary: "#ecb501",
        secondary: "#2d3338",
      },
    });
  };

  // Generate realistic variable names based on endpoint (/products -> products, /users/1 -> user)
  const getVarName = (endpoint) => {
    const parts = endpoint.split("/").filter(Boolean);
    let name = parts[0] || "data";
    if (parts.length > 1 && !isNaN(parts[1]) && name.endsWith("s")) {
      name = name.slice(0, -1);
    }
    return name;
  };

  const varName = getVarName(selectedEndpoint);
  const setterName = `set${varName.charAt(0).toUpperCase() + varName.slice(1)}`;
  // If endpoint ends with a number (e.g. /products/1), the state is likely an object (null). Otherwise, an array ([]).
  const initialState = selectedEndpoint.split("/").length > 2 ? "null" : "[]";

  const fetchCode = `import React, { useState, useEffect } from "react";

const DemoComponent = () => {
  const [${varName}, ${setterName}] = useState(${initialState});

  useEffect(() => {
    fetch("https://fakestoreapi.noksha.dev/api${selectedEndpoint}")
      .then((res) => res.json())
      .then((json) => ${setterName}(json.data || json))
      .catch((error) => console.error("Error fetching ${varName}:", error));
  }, []);

  return (
    <div>
      {/* Map over ${varName} here */}
    </div>
  );
};`;

  const tsCode = `import React, { useState, useEffect } from "react";

// Define your interface based on the endpoint
interface DataModel {
  id: number;
  [key: string]: any;
}

const DemoComponent: React.FC = () => {
  const [${varName}, ${setterName}] = useState<DataModel${initialState === "[]" ? "[]" : " | null"}>(${initialState});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://fakestoreapi.noksha.dev/api${selectedEndpoint}");
        const json = await response.json();
        ${setterName}(json.data || json);
      } catch (error) {
        console.error("Error fetching ${varName}:", error);
      }
    };
    fetchData();
  }, []);

  return <div>{/* Map over ${varName} here */}</div>;
};`;

  const pythonCode = `import requests

def fetch_data():
    url = "https://fakestoreapi.noksha.dev/api${selectedEndpoint}"
    try:
        response = requests.get(url)
        response.raise_for_status()
        
        # Access the JSON payload
        data = response.json()
        print(data)
    except requests.exceptions.HTTPError as err:
        print(f"HTTP error occurred: {err}")
    except Exception as err:
        print(f"An error occurred: {err}")

if __name__ == "__main__":
    fetch_data()`;

  const curlCode = `curl -X GET "https://fakestoreapi.noksha.dev/api${selectedEndpoint}" \\
  -H "Accept: application/json"`;

  const javaCode = `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class FetchData {
    public static void main(String[] args) {
        try {
            HttpClient client = HttpClient.newHttpClient();
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create("https://fakestoreapi.noksha.dev/api${selectedEndpoint}"))
                .GET()
                .header("Accept", "application/json")
                .build();

            HttpResponse<String> response = client.send(
                request, HttpResponse.BodyHandlers.ofString()
            );

            System.out.println("Status Code: " + response.statusCode());
            System.out.println("Response Body: " + response.body());
        } catch (Exception e) {
            e.printStackTrace();
        }
    }
}`;

  const phpCode = `<?php

$url = "https://fakestoreapi.noksha.dev/api${selectedEndpoint}";

$curl = curl_init($url);
curl_setopt($curl, CURLOPT_URL, $url);
curl_setopt($curl, CURLOPT_RETURNTRANSFER, true);

// For testing purposes only: disable SSL verification
// curl_setopt($curl, CURLOPT_SSL_VERIFYHOST, false);
// curl_setopt($curl, CURLOPT_SSL_VERIFYPEER, false);

$resp = curl_exec($curl);
$httpcode = curl_getinfo($curl, CURLINFO_HTTP_CODE);

if ($e = curl_error($curl)) {
    echo "cURL error: " . $e;
} else {
    echo "Status Code: " . $httpcode . "\\n";
    $decoded = json_decode($resp, true);
    print_r($decoded);
}

curl_close($curl);

?>`;

  const getActiveCode = () => {
    switch (activeLanguage) {
      case "javascript":
        return fetchCode;
      case "typescript":
        return tsCode;
      case "python":
        return pythonCode;
      case "curl":
        return curlCode;
      case "java":
        return javaCode;
      case "php":
        return phpCode;
      default:
        return fetchCode;
    }
  };

  const getActiveLanguageString = () => {
    switch (activeLanguage) {
      case "javascript":
        return "javascript";
      case "typescript":
        return "typescript";
      case "python":
        return "python";
      case "curl":
        return "bash";
      case "java":
        return "java";
      case "php":
        return "php";
      default:
        return "javascript";
    }
  };

  const languages = [
    { id: "javascript", label: "JavaScript" },
    { id: "typescript", label: "TypeScript" },
    { id: "python", label: "Python" },
    { id: "curl", label: "Curl" },
    { id: "php", label: "PHP" },
    { id: "java", label: "Java" },
  ];

  return (
    <div className="w-full bg-white text-primeColor px-4 pb-20 pt-10 font-titleFont">
      <div className="max-w-7xl mx-auto flex flex-col gap-12">
        {/* SEO Header Section */}
        <div className="text-center max-w-3xl mx-auto flex flex-col gap-4 animate-fade-in-up">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-primeColor tracking-tight">
            Build Robust{" "}
            <span className="text-designColor">Project Data Structures</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed font-bodyFont">
            Instantly prototype your Next.js, React, or Vue applications using
            the most reliable <strong>Fake Store API</strong>. Our free mock
            data endpoints ensure you can build e-commerce interfaces without
            waiting on backend development.
          </p>
        </div>

        <div className="flex flex-col gap-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 border-b border-gray-200 pb-4 animate-fade-in-up animation-delay-100 relative z-40">
            <h1 className="text-xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primeColor to-gray-600">
              Try Fakestore API Fake Data
            </h1>

            {/* Custom Endpoint Selector */}
            <div
              className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto relative z-50"
              ref={dropdownRef}
            >
              <label className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                Select Endpoint:
              </label>
              <div className="relative w-full max-w-64 lg:w-72">
                <div
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm text-primeColor font-titleFont font-medium cursor-pointer flex justify-between items-center transition-all hover:bg-gray-50 focus:border-primeColor"
                >
                  <span className="truncate flex items-center gap-2">
                    {endpoints.find((e) => e.value === selectedEndpoint)?.label}
                    <span className="text-gray-400 text-xs font-normal hidden sm:inline">
                      ({selectedEndpoint})
                    </span>
                  </span>
                  <FaChevronDown
                    className={`text-gray-400 text-sm transition-transform duration-300 ${isDropdownOpen ? "rotate-180 text-designColor" : ""}`}
                  />
                </div>

                {isDropdownOpen && (
                  <div
                    className="absolute z-[100] w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-72 overflow-y-auto custom-scrollbar animate-fade-in-up"
                    style={{
                      animationDuration: "0.2s",
                      animationFillMode: "both",
                    }}
                  >
                    <div className="p-1.5 flex flex-col gap-1">
                      {endpoints.map((ep) => (
                        <div
                          key={ep.value}
                          className={`px-3 py-2 cursor-pointer rounded-lg font-titleFont text-sm transition-colors duration-200 ${
                            selectedEndpoint === ep.value
                              ? "bg-designColor/10 text-designColor font-semibold"
                              : "text-primeColor hover:bg-gray-100 hover:text-designColor"
                          }`}
                          onClick={() => {
                            setSelectedEndpoint(ep.value);
                            setShowFakeData(false);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="flex justify-between items-center w-full">
                            <span>{ep.label}</span>
                            <span
                              className={`text-xs ${selectedEndpoint === ep.value ? "text-designColor/70" : "text-gray-400"}`}
                            >
                              {ep.value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left Side: Code Examples */}
            <div className="flex flex-col animate-fade-in-up animation-delay-200 w-full">
              {/* Language Tabs - Top of Editor */}
              <div className="flex flex-wrap items-center gap-2 mb-4 bg-gray-100/50 p-1.5 rounded-xl border border-gray-200 shadow-sm w-full">
                {languages.map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => setActiveLanguage(lang.id)}
                    className={`flex-1 sm:flex-none px-4 py-2 flex items-center justify-center text-sm font-bold rounded-lg transition-all duration-300 text-center border ${
                      activeLanguage === lang.id
                        ? "bg-primeColor text-white border-primeColor shadow-md pointer-events-none"
                        : "bg-white text-gray-500 border-gray-200 hover:text-primeColor hover:border-primeColor shadow-sm cursor-pointer"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>

              <div className="w-full h-[450px] flex flex-col bg-[#1E1E1E] rounded-xl shadow-2xl overflow-hidden relative group hover:shadow-[0_10px_30px_rgba(236,181,1,0.15)] transition-all duration-500 mb-6">
                {/* IDE-Style Header */}
                <div className="bg-[#2D2D2D] px-4 py-3 border-b border-gray-700 flex items-center justify-between gap-3 flex-shrink-0">
                  <div className="flex gap-2 shrink-0 mr-2">
                    <span className="w-3 h-3 rounded-full bg-red-500 shadow-sm border border-red-400"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-500 shadow-sm border border-yellow-400"></span>
                    <span className="w-3 h-3 rounded-full bg-green-500 shadow-sm border border-green-400"></span>
                  </div>

                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest shrink-0">
                    {languages.find((l) => l.id === activeLanguage)?.label}{" "}
                    Setup
                  </h3>
                </div>

                {/* Editor Content */}
                <div className="flex-1 overflow-auto custom-scrollbar bg-[#18181A] relative p-6 text-sm">
                  <SyntaxHighlighter
                    language={getActiveLanguageString()}
                    style={dracula}
                    customStyle={{
                      background: "transparent",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {getActiveCode()}
                  </SyntaxHighlighter>
                  <button
                    onClick={() => copyToClipboard(getActiveCode())}
                    className="absolute top-4 right-4 bg-[#2D2D2D] p-2 rounded-md text-gray-400 hover:text-white hover:bg-designColor transition-all duration-300 shadow-lg"
                    aria-label="Copy Fetch Code"
                  >
                    <FaCopy size={16} />
                  </button>
                </div>
              </div>

              <div className="flex justify-start">
                <button
                  onClick={handleFakeData}
                  disabled={loadingState}
                  className="relative overflow-hidden group w-44 h-14 bg-primeColor text-white font-bold rounded-lg hover:bg-designColor hover:text-primeColor hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 shrink-0"
                >
                  <span className="relative z-10 transition-transform duration-300 group-hover:scale-110">
                    {loadingState ? "Fetching..." : "Test Endpoint"}
                  </span>
                  <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-[150%] skew-x-[-20deg] group-hover:animate-shine"></div>
                </button>
              </div>
            </div>

            {/* Right Side: Demo Response */}
            <div className="flex flex-col animate-fade-in-up animation-delay-300 w-full h-full">
              {/* Spacer/Header - Top of Editor to mirror Left Side Tabs layout */}
              <div className="hidden lg:flex items-center justify-between mb-4 bg-gray-50/50 p-2 rounded-xl border border-gray-200/60 shadow-sm w-full h-[54px] opacity-0 pointer-events-none">
                <span className="text-sm font-bold text-transparent px-2">
                  Spacer
                </span>
              </div>

              {loadingState ? (
                <div className="w-full h-[450px] bg-white border border-gray-200 shadow-sm rounded-xl flex justify-center items-center mb-6">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-gray-200 border-t-designColor rounded-full animate-spin"></div>
                    <span className="text-primeColor font-titleFont font-medium tracking-wide animate-pulse">
                      Retrieving Mock Data...
                    </span>
                  </div>
                </div>
              ) : error ? (
                <div className="w-full h-[450px] bg-red-50 border border-red-100 rounded-xl flex justify-center items-center p-6 text-center mb-6">
                  <p className="text-red-500 font-semibold">{error}</p>
                </div>
              ) : showFakeData && fakeData ? (
                <div className="w-full h-[450px] flex flex-col bg-[#1E1E1E] rounded-xl shadow-2xl overflow-hidden relative group hover:shadow-[0_10px_30px_rgba(236,181,1,0.15)] transition-all duration-500 mb-6">
                  <div className="bg-[#2D2D2D] px-4 py-3 border-b border-gray-700 flex flex-col xl:flex-row justify-between items-center gap-4 xl:gap-0 flex-shrink-0">
                    <h3 className="text-sm font-semibold text-gray-300 tracking-wider">
                      Live JSON Response
                    </h3>
                    <div className="flex items-center gap-3 w-full xl:w-auto overflow-x-auto pb-2 xl:pb-0 custom-scrollbar justify-center xl:justify-end">
                      <button
                        onClick={() => setIsLineWrapped(!isLineWrapped)}
                        className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-md transition-colors duration-300 whitespace-nowrap ${
                          isLineWrapped
                            ? "text-white bg-designColor/20 border border-designColor border-opacity-30"
                            : "text-gray-400 hover:text-gray-200 bg-gray-800"
                        }`}
                        title={
                          isLineWrapped
                            ? "Disable Line Wrap"
                            : "Enable Line Wrap"
                        }
                      >
                        {isLineWrapped ? (
                          <FaAlignJustify size={12} />
                        ) : (
                          <FaAlignLeft size={12} />
                        )}
                        Wrap
                      </button>

                      <button
                        onClick={() => setIsLivePreviewOpen(true)}
                        className="flex items-center gap-2 text-xs font-bold text-designColor hover:text-white bg-designColor/10 hover:bg-designColor px-3 py-1.5 rounded-md transition-colors duration-300 shadow-[0_0_10px_rgba(236,181,1,0.1)] hover:shadow-[0_0_15px_rgba(236,181,1,0.3)] whitespace-nowrap"
                      >
                        <FaRegEye size={14} />
                        View UI
                      </button>
                    </div>
                  </div>
                  <div
                    className={`flex-1 p-6 text-sm custom-scrollbar bg-[#18181A] ${isLineWrapped ? "overflow-y-auto overflow-x-hidden" : "overflow-auto"}`}
                  >
                    <SyntaxHighlighter
                      language="json"
                      style={dracula}
                      wrapLines={isLineWrapped}
                      wrapLongLines={isLineWrapped}
                      lineProps={
                        isLineWrapped
                          ? {
                              style: {
                                wordBreak: "break-all",
                                whiteSpace: "pre-wrap",
                              },
                            }
                          : {}
                      }
                      customStyle={{
                        background: "transparent",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(fakeData, null, 2)}
                    </SyntaxHighlighter>
                  </div>
                </div>
              ) : (
                <div className="w-full h-[450px] bg-[#F9FAFB] border-2 border-dashed border-gray-200 rounded-xl flex flex-col justify-center items-center p-10 text-center hover:border-designColor transition-colors duration-300 mb-6 shadow-inner">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-primeColor mb-2">
                    Awaiting Request
                  </h4>
                  <p className="text-gray-500 font-bodyFont text-sm max-w-sm">
                    Click the "Test Endpoint" button to fire a live GET request
                    and see the structure of our JSON responses.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live Preview Sidebar */}
      <LivePreview
        isOpen={isLivePreviewOpen}
        onClose={() => setIsLivePreviewOpen(false)}
        data={fakeData}
        endpoint={selectedEndpoint}
      />
    </div>
  );
};

export default ExampleCode;
